import { format as dateFnsFormat, formatDistanceToNow, parseISO } from 'date-fns'
import { enUS, ptBR, es, pt, fr, frCA, frCH } from 'date-fns/locale'

const localeMap = {
  'en-US': enUS,
  'pt-BR': ptBR,
  'pt-PT': pt,
  'es-ES': es,
  'es-MX': es,
  'es-AR': es,
  'fr-FR': fr,
  'fr-BE': fr,
  'fr-CA': frCA,
  'fr-CH': frCH,
}

export function getDateFnsLocale(locale: string) {
  return localeMap[locale as keyof typeof localeMap] || enUS
}

export function formatDate(date: Date | string, formatStr: string, locale: string): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  return dateFnsFormat(dateObj, formatStr, { locale: getDateFnsLocale(locale) })
}

export function formatRelativeTime(date: Date | string, locale: string): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  return formatDistanceToNow(dateObj, { 
    addSuffix: true,
    locale: getDateFnsLocale(locale) 
  })
}

export type DateStyle = 'short' | 'long' | 'dateTime' | 'dayMonth'

const DATE_STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  /* "16 de set. de 2026" */
  short: { day: 'numeric', month: 'short', year: 'numeric' },
  /* "6 de novembro de 2026" */
  long: { day: 'numeric', month: 'long', year: 'numeric' },
  /* "24 de out. de 2026, 03:09" */
  dateTime: { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  /* "26 de out." */
  dayMonth: { day: 'numeric', month: 'short' }
}

/**
 * The one date formatter (UX review T6): every screen writes dates the same
 * way, in the reader's language and, when given, the community's time zone.
 * Never "DD/MM/AAAA, HH:mm" nor "26 out 2026".
 */
export function formatDateStyle(date: Date | string | null | undefined, style: DateStyle, locale: string, timeZone?: string | null): string {
  if (!date) return ''
  const value = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(value.getTime())) return ''
  return new Intl.DateTimeFormat(locale, { ...DATE_STYLES[style], ...(timeZone ? { timeZone } : {}) }).format(value)
}

export function formatShortDate(date: Date | string, locale: string): string {
  return formatDateStyle(date, 'short', locale)
}

export function formatLongDate(date: Date | string, locale: string): string {
  return formatDateStyle(date, 'long', locale)
}

export function formatDateTime(date: Date | string, locale: string): string {
  return formatDateStyle(date, 'dateTime', locale)
}

export function formatNumber(value: number, locale: string, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(locale, options).format(value)
}

export function formatCompactNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

export function formatPercentage(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value / 100)
}
